import axios, { AxiosInstance, AxiosResponse } from 'axios';
import __LocalStorage from './localData';

const baseURL = process.env.REACT_APP_API_URL;
const TIME_OUT = 60000;
const api: AxiosInstance = axios.create({
  baseURL,
  timeout: TIME_OUT,
});

type ApiResponse<T> = {
  data?: T;
  error?: string;
  statusCode?: number;
  meta?: any;
  message?: string;
};

// Request interceptor for token
api.interceptors.request.use(
  (config) => {
    const token = __LocalStorage('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Check for 401 status
    const status = error.response?.status;
    switch (status) {
      case 401:
        if (window.location.href.includes('/signIn')) break;
        else {
          __LocalStorage('accessToken', null, true);
          window.location.href = '/signIn';
          break;
        }

      case 403:
        window.location.href = '/';
        break;

      default:
        console.log('Http error status', status);
    }

    return Promise.reject(error);
  },
);

// Centralized response handling
const handleResponse = <T>(response: AxiosResponse<ApiResponse<T>>): ApiResponse<T> => {
  const { data, error, statusCode, meta, message } = response.data;
  return { data, error, statusCode, meta, message };
};

// Error handling
const handleError = (error: any): ApiResponse<never> => {
  if (axios.isAxiosError(error)) {
    return {
      error: error.response?.data?.message || error.message,
      statusCode: error.response?.status || 500,
    };
  }
  return { error: 'An unknown error occurred' };
};

export const ApiService = async <T>(
  path: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  data?: any,
): Promise<ApiResponse<T>> => {
  try {
    const response = await api.request<ApiResponse<T>>({
      url: path,
      method,
      data,
    });

    return handleResponse(response);
  } catch (error) {
    return handleError(error);
  }
};
